var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { fetch } from '@mitchell/typescript.service.fetch';
import { EnvironmentSettings } from 'mi.common.settings.environment.contract';
import { VehicleSelectionConstants } from '../constants/index';
import { CaseFormatter, getValueFromSessionStorage, SessionStorageKey } from "../../common";
const Commercial = 'Commercial';
/**
 * Handles vehicle selection / decode api calls.
 */
export class VehicleSelectionService {
    // Don - In the event fetch is not provided the default fetch implementation which we created is used.
    // Except in testing situations this default will always be used (since no DI framework exists to resolve this)
    // so it's inpractical to write a test for a condition designed for testing.
    /* istanbul ignore next */
    constructor(fetchWrapper = fetch) {
        this.fetchWrapper = fetchWrapper;
    }
    getRequest() {
        const request = {
            method: 'GET'
        };
        return request;
    }
    isCommercialGenericVehicle(typeId, model) {
        if (typeId !== undefined && VehicleSelectionConstants.commercialGenericVehicleTypeIds.includes(typeId)) {
            return true;
        }
        return model.includes(Commercial);
    }
    /**
     * Decodes with the given uri and vehicle info data. If use vin is true the vin is included in the request.
     * @param vehicleInfo The current vehicle info data.
     * @param nextUri The next URI to decode with.
     * @param useVin True if the vehicle info data's vin should be included in the request, otherwise false.
     */
    decodeWithUri(vehicleInfo, nextUri, useVin = true) {
        return __awaiter(this, void 0, void 0, function* () {
            let uri = nextUri || '/VehicleInfo?';
            if (vehicleInfo.Vin && useVin) {
                uri += '&vin=' + vehicleInfo.Vin;
            }
            uri = this.addOwnerOrgKeyParam(uri);
            return this.fetchWrapper(EnvironmentSettings.get(window.mitchell.environment).vehicleSelection +
                VehicleSelectionConstants.serviceVersion +
                uri, this.getRequest())
                .then(response => response.json())
                .then(response => {
                const ret = Object.assign(Object.assign({}, vehicleInfo), response); //  Don - Not a worthwhile use of time to test these conditionals
                // Restore the VIN back on to the decoded vehicle in case downstream users want it there.
                // Don - Just a null check. Not much gain in testing the conditional
                /* istanbul ignore next */
                if (ret.Vehicle && ret.Vehicle.Value) {
                    ret.Vehicle.Value.Vin = ret.Vin || '';
                }
                return ret;
            });
        });
    }
    /**
     * Decodes with the provided vin and vid.
     * @param vehicleInfo The current vehicle info data object.
     * @param vin The vin to decode with.
     * @param vid The vid to decode with.
     */
    decodeWithVin(vehicleInfo, vin, vid) {
        return __awaiter(this, void 0, void 0, function* () {
            let uri = '/VehicleInfo?';
            if (vin) {
                uri += 'vin=' + vin;
            }
            if (vid) {
                if (vin) {
                    uri += '&';
                }
                uri += 'vid=' + vid;
            }
            uri = this.addOwnerOrgKeyParam(uri);
            return this.fetchWrapper(EnvironmentSettings.get(window.mitchell.environment).vehicleSelection +
                VehicleSelectionConstants.serviceVersion +
                uri, this.getRequest())
                .then(response => response.json())
                .then(response => {
                const ret = Object.assign(Object.assign({}, vehicleInfo), response);
                // Add the VIN back onto vehicle info in case it wasn't there already. Needed for the VSM.
                ret.Vin = vin;
                // Restore the VIN back on to the decoded vehicle in case downstream users want it there.
                /* istanbul ignore next */
                if (ret.Vehicle && ret.Vehicle.Value) {
                    ret.Vehicle.Value.Vin = vin;
                }
                return ret;
            });
        });
    }
    /**
     * Decodes a generic vehicle with the provided information. Note that "decodeWithUri" will also produce generic decodes with the correct information.
     * @param vehicleInfo The current vehicle info data object.
     * @param model The model name.
     * @param year The year of the vehicle.
     * @param typeId The type ID of the vehicle.
     */
    decodeGeneric(vehicleInfo, model, year, typeId) {
        return __awaiter(this, void 0, void 0, function* () {
            const isCommercial = this.isCommercialGenericVehicle(typeId, model);
            const vTypeId = isCommercial
                ? VehicleSelectionConstants.genericDecodeTypeIds[1]
                : VehicleSelectionConstants.genericDecodeTypeIds[0];
            let uri = '/VehicleInfo?model=' + model + '&year=' + year + '&type=' + vTypeId;
            uri = this.addOwnerOrgKeyParam(uri);
            return this.fetchWrapper(EnvironmentSettings.get(window.mitchell.environment).vehicleSelection +
                VehicleSelectionConstants.serviceVersion +
                uri, this.getRequest())
                .then(response => response.json())
                .then(response => {
                const ret = Object.assign(Object.assign({}, vehicleInfo), response);
                ret.Types = ret.Types.filter(type => {
                    return type.Id === vTypeId;
                });
                ret.Years = ret.Years.filter(y => {
                    return y.Id === ret.Vehicle.Value.Year.Id;
                });
                ret.Models = ret.Models.filter(m => {
                    return m.Value === ret.Vehicle.Value.Model.Value;
                });
                // Restore the VIN back on to the decoded vehicle in case downstream users want it there.
                /* istanbul ignore next */
                if (ret.Vehicle && ret.Vehicle.Value) {
                    ret.Vehicle.Value.Vin = ret.Vin || '';
                }
                return ret;
            });
        });
    }
    addOwnerOrgKeyParam(uri) {
        const ownerOrgKey = getValueFromSessionStorage(SessionStorageKey.CURRENT_SHOP_KEY);
        if (ownerOrgKey) {
            uri += uri.includes('?') ? `&OwnerOrgKey=${ownerOrgKey}` : `?OwnerOrgKey=${ownerOrgKey}`;
        }
        return uri;
    }
    getAllVehicleMakesAndModels() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `${EnvironmentSettings.get(window.mitchell.environment).vehicleSelection +
                VehicleSelectionConstants.serviceVersion}/VehicleInfo/Makesandmodels`;
            const response = yield this.fetchWrapper(url, this.getRequest());
            const jsonResponseObject = (yield response.json()).Makes;
            return CaseFormatter.pascalToCamelCase(jsonResponseObject);
        });
    }
}
//# sourceMappingURL=VehicleSelectionService.js.map